import { Link } from "react-router-dom";
import { ArrowUpRight, BedDouble, GitCompareArrows, Ruler, Users } from "lucide-react";
import Stars from "@/components/site/Stars";
import { useLang } from "@/lib/i18n";
import { useCompare } from "@/lib/compare";
import { BLOCKS, PRICE_KEY, fmtUSD, imgSrc } from "@/lib/api";
import type { Yacht } from "@/types";

interface Props { yacht: Yacht; query?: string; featured?: boolean }

export default function YachtCard({ yacht, query = "", featured = false }: Props) {
  const { t } = useLang();
  const cmp = useCompare();
  const selected = cmp.has(yacht.id);
  return (
    <Link to={`/yachts/${yacht.id}${query}`} data-testid={`yacht-card-${yacht.id}`}
      className="group flex flex-col bg-white rounded-xl overflow-hidden border border-slate-200/80 transition-shadow duration-500 hover:shadow-[0_30px_60px_-30px_rgba(11,25,44,0.45)]">
      <div className={`relative overflow-hidden ${featured ? "aspect-[16/10]" : "aspect-[4/3]"}`}>
        <img src={imgSrc(yacht.photos[0])} alt={yacht.name} loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B192C]/70 via-transparent to-transparent" />
        <span className="absolute top-4 left-4 rounded-full bg-white/90 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#0B192C]">{t(`type.${yacht.type}`)}</span>
        {yacht.rating && (
          <span data-testid={`yacht-card-rating-${yacht.id}`} className="absolute top-4 right-4 inline-flex items-center gap-1.5 rounded-full bg-[#0B192C]/70 backdrop-blur-sm px-2.5 py-1 text-[11px] font-semibold text-white">
            <Stars value={yacht.rating.avg} size="h-3 w-3" testId={`yacht-card-stars-${yacht.id}`} />{yacht.rating.avg.toFixed(1)} ({yacht.rating.count})
          </span>
        )}
        <div className="absolute bottom-4 left-5 right-5 flex items-end justify-between text-white">
          <h3 className="font-display text-2xl sm:text-3xl">{yacht.name}</h3>
          <span className="text-right text-xs text-white/80">{t("card.from")}<br /><b data-testid={`yacht-card-price-${yacht.id}`} className="font-display text-xl text-[#F3E5AB]">{fmtUSD(yacht.prices.h4)}</b></span>
        </div>
      </div>
      <div className="flex flex-1 flex-col p-5 sm:p-6">
        <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-600">
          <span className="flex items-center gap-1.5"><Ruler className="h-4 w-4 text-[#00A8A0]" />{yacht.length_ft} {t("card.ft")}</span>
          <span className="flex items-center gap-1.5"><Users className="h-4 w-4 text-[#00A8A0]" />{yacht.capacity} {t("card.guests")}</span>
          <span className="flex items-center gap-1.5"><BedDouble className="h-4 w-4 text-[#00A8A0]" />{yacht.cabins} {t("card.cabins")}</span>
        </div>
        <div className="mt-5 grid grid-cols-4 gap-2">
          {BLOCKS.map((b) => (
            <div key={b} className="rounded-md bg-slate-50 border border-slate-100 px-2 py-2 text-center">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">{t(`block.${b}`)}</p>
              <p className="mt-0.5 text-xs sm:text-sm font-semibold text-[#0B192C]">{fmtUSD(yacht.prices[PRICE_KEY[b]])}</p>
            </div>
          ))}
        </div>
        <span className="mt-6 flex items-center justify-between gap-3">
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#0B192C] group-hover:text-[#008C86] transition-colors">
            {t("card.view")}<ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </span>
          <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); cmp.toggle(yacht.id); }} data-testid={`yacht-card-compare-${yacht.id}`} aria-pressed={selected}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-semibold transition-colors ${selected ? "border-[#0B192C] bg-[#0B192C] text-white" : "border-slate-200 text-slate-500 hover:border-[#0B192C] hover:text-[#0B192C]"}`}>
            <GitCompareArrows className="h-3.5 w-3.5" />{selected ? t("cmp.added") : t("cmp.add")}
          </button>
        </span>
      </div>
    </Link>
  );
}
